import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import styles from './styles.module.css';

// Register Chart.js elements
ChartJS.register(ArcElement, Tooltip, Legend);

const DonutChart: React.FC<{ data: number[] }> = ({ data }) => {
  const total = data.reduce((acc, value) => acc + value, 0);

  const chartData = {
    labels: ["Not Recommended", "Cautiously Recommended", "Recommended", "Highly Recommended", "Disqualified" ],
    datasets: [
      {
        data,
        backgroundColor: ["#E03923", "#BCDCAB", "#619C41", "#2B451D", "#E8E8EB"],
        hoverOffset: 10,
        spacing: 5,
      },
    ],
  };

  const options = {
    cutout: "70%",
    plugins: {
      tooltip: { enabled: false },
      legend: { display: false },
    },
    elements: {
      arc: { borderWidth: 0, borderRadius: 5 },
    },
  };

  const chartRadius = 110; // Approximate radius of the chart
  const labelRadius = chartRadius + 10; // Reduced distance from center for labels

  return (
    <div style={{ position: "relative", width: 220, height: 220 }}>
      {/* Doughnut Chart */}
      <Doughnut data={chartData} options={options} />

      {/* Custom Center Content */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
        }}
      >
        <span style={{ fontSize: "24px", fontWeight: "bold", color: "black" }}>
          {total}
        </span>
        <br />
        <span style={{ fontSize: "14px", color: "#6B7280" }}>Completed</span>
      </div>

       {/* zero data circular placeholder */}
      {total <=0 && <div className={styles.empty_circle}></div>}

      {/* Custom Labels */}
      {data.map((value, index) => {
        // Skip rendering if total is 0 to avoid NaN percentages
        if (total === 0) return null;

        // Calculate cumulative sum up to the current segment
        const cumulativeSum = data.slice(0, index).reduce((sum, val) => sum + val, 0);

        // Correctly align the starting position of the first segment
        const startAngle = (cumulativeSum / total) * 2 * Math.PI - Math.PI / 2;
        const endAngle = ((cumulativeSum + value) / total) * 2 * Math.PI - Math.PI / 2;

        // Calculate label angle (middle of the segment)
        const angle = (startAngle + endAngle) / 2;

        // Calculate label position closer to the chart
        const x = labelRadius * Math.cos(angle);
        const y = labelRadius * Math.sin(angle);

        return (
          <div
            key={index}
            style={{
              position: "absolute",
              top: `calc(50% + ${y}px)`,
              left: `calc(50% + ${x}px)`,
              transform: "translate(-50%, -50%)",
              minWidth: '56px',
            }}
          >
            <div
              style={{
                minHeight: '22px',
                border: `1px solid ${chartData?.datasets[0]?.backgroundColor[index] || 'black'}`,
                background: "#fff",
                borderRadius: "12px",
                padding: "2px 6px",
                fontSize: "12px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                textAlign: "center",
              }}
            >
              {value} ({((value / total) * 100).toFixed(0)}%)
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DonutChart;
